import classNames from 'classnames';
import { memo } from 'react';
import isEqual from 'react-fast-compare';

import { PlayIcon, SkipForwardIcon, XIcon } from '@air/design-icons';
import {
  FileStatusButton,
  FileStatusPanelItem,
  FileStatusSubtitle,
  FileStatusThumbnail,
  FileStatusTitle,
  type FileTrackingItem,
} from '@air/feature-file-status/ui';
import { getSubtitleWithProgress } from '@air/feature-file-status/utils';

import { SHARED_PANEL_ITEM_CLASS_NAMES } from '../../constants';

export type FileStatusTrackingPanelListItemPausedProps = {
  item: FileTrackingItem;
  onCancel?: (item: FileTrackingItem) => void;
  onResume?: (item: FileTrackingItem) => void;
  onSkip?: (item: FileTrackingItem) => void;
};

export const FileStatusTrackingPanelListItemPaused = memo(
  ({
    onSkip,
    onCancel,
    onResume,
    item,
    item: {
      title,
      onRowClick,
      previewLoader,
      avatar,
      thumbnailSource,
      subtitle,
      sizeInBytes,
      ext,
      currentBytesUploaded,
      status,
    },
  }: FileStatusTrackingPanelListItemPausedProps) => (
    <FileStatusPanelItem
      avatar={avatar ?? <FileStatusThumbnail ext={ext} status={status} thumbnailSource={thumbnailSource} />}
      buttons={
        <>
          {onResume && (
            // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
            <FileStatusButton appearance="ghost" label="Resume" onClick={() => onResume(item)}>
              <PlayIcon size="sm" />
            </FileStatusButton>
          )}
          {onSkip && (
            // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
            <FileStatusButton appearance="ghost" label="Skip" onClick={() => onSkip(item)}>
              <SkipForwardIcon size="sm" />
            </FileStatusButton>
          )}
          {onCancel && (
            // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
            <FileStatusButton appearance="ghost" label="Cancel" onClick={() => onCancel(item)}>
              <XIcon size="sm" />
            </FileStatusButton>
          )}
        </>
      }
      className={classNames('hover:bg-gray-3', SHARED_PANEL_ITEM_CLASS_NAMES, !!onRowClick && 'cursor-pointer')}
      onClick={onRowClick}
      previewLoader={previewLoader}
      subtitle={
        <FileStatusSubtitle className="truncate">
          {subtitle ?? getSubtitleWithProgress({ sizeInBytes, title: 'Paused', uploadedBytes: currentBytesUploaded })}
        </FileStatusSubtitle>
      }
      title={<FileStatusTitle className="text-12">{title}</FileStatusTitle>}
    />
  ),
  isEqual,
);

FileStatusTrackingPanelListItemPaused.displayName = 'FileStatusTrackingPanelListItemPaused';
